import { HttpClient } from '@wix/http-client';
import {
  queryLibraryItems,
  createFolder,
  authorizeActions,
  _delete,
  reportLibraryItem,
  share,
  download,
  rename,
  favorite,
  viewFolder,
  getFolderTree,
  move,
  viewFile,
  startFileUpload,
  completeFileUpload,
  queryLibraryFolderItems,
} from '@wix/ambassador-file-sharing-v1-library-item/http';
import {
  requestAccess,
  listRoles,
} from '@wix/ambassador-file-sharing-v1-role/http';
import { getAppSettings } from '../Services/AppSettings.service';

export class FileSharingService {
  fileSharingService: any;
  instance: string = '';
  baseUrl: string = '';
  private ready: Promise<void>;
  private resolveReady!: () => void;
  constructor() {
    this.ready = new Promise<void>((resolve) => {
      this.resolveReady = resolve;
    });
    getAppSettings().subscribe((data: any) => {
      this.instance = data?.instance;
      // Editor: hit the wixapps data host (the instance token scopes the tenant) — same host the
      // Settings service uses; window.location.origin in the editor is the editor host, which
      // doesn't serve the File Sharing API. Preview: the previewed site's own origin (matches
      // file-share-dashboard-bm's getFileSharingClient). Live: the controller-computed base URL.
      if (data?.viewMode === 'Editor') {
        this.baseUrl = 'https://editor.wixapps.net';
      } else if (data?.viewMode === 'Preview') {
        this.baseUrl =
          typeof window !== 'undefined'
            ? window.location.origin
            : data?.baseUrl;
      } else {
        this.baseUrl = data?.baseUrl;
      }
      if (this.instance) {
        this.fileSharingService = new HttpClient({
          baseURL: this.baseUrl,
          headers: { Authorization: this.instance },
        });
        this.resolveReady();
      }
    });
  }

  private request = async (operation: any) => {
    await this.ready;
    return this.fileSharingService.request(operation);
  };

  getListRoles = async (query: any) => {
    try {
      const result = await this.request(listRoles(query));
      return result;
    } catch (e) {
      const error: any = e;
      return error;
    }
  };
  getFolderById = async (folderId: string, sortBy: any) => {
    try {
      const result = await this.request(
        queryLibraryItems({
          filter: {
            parentLibraryItemIds: [folderId],
          },
          sort: {
            orientation: sortBy.orientation,
            sortBy: sortBy.sortBy,
          },
        }),
      );
      return result;
    } catch (e) {
      const error: any = e;
      return error;
    }
  };

  loadMoreItemsApi = async (folderId: string, cursor: string) => {
    try {
      const result = await this.request(
        queryLibraryItems({
          filter: {
            parentLibraryItemIds: [folderId],
          },
          paging: {
            cursor,
          },
        }),
      );
      return result;
    } catch (e) {
      const error: any = e;
      return error;
    }
  };

  getFolderInfoById = async (folderId: string) => {
    try {
      const result = await this.request(
        queryLibraryItems({
          filter: {
            itemIds: [folderId],
          },
        }),
      );
      return result;
    } catch (e) {
      const error: any = e;
      return error;
    }
  };
  searchByValue = async (query: any) => {
    try {
      return await this.request(queryLibraryItems(query));
    } catch (e) {
      return e;
    }
  };
  createFolder = async (query: any) => {
    try {
      return await this.request(createFolder(query));
    } catch (e) {
      return e;
    }
  };
  authorizeActions = async (query: any) => {
    try {
      return await this.request(authorizeActions(query));
    } catch (e) {
      return e;
    }
  };
  delete = async (query: any) => {
    try {
      return await this.request(_delete(query));
    } catch (e) {
      return e;
    }
  };
  report = async (query: any) => {
    try {
      return await this.request(
        reportLibraryItem({ action: query }),
      );
    } catch (e) {
      return e;
    }
  };
  shareItem = async (query: any) => {
    try {
      return await this.request(share(query));
    } catch (e) {
      return e;
    }
  };
  downloadItems = async (query: any) => {
    try {
      return await this.request(download(query));
    } catch (e) {
      return e;
    }
  };

  renameItem = async (query: any) => {
    try {
      return await this.request(rename(query));
    } catch (e) {
      return e;
    }
  };

  favoriteChangeAPI = async (query: any) => {
    try {
      return await this.request(favorite(query));
    } catch (e) {
      return e;
    }
  };

  addViewFolderAPI = async (query: any) => {
    try {
      return await this.request(viewFolder(query));
    } catch (e) {
      return e;
    }
  };
  getFolderTree = async (query: any) => {
    try {
      return await this.request(getFolderTree(query));
    } catch (e) {
      // setMoveLoader(false);
      return e;
    }
  };
  moveItems = async (query: any) => {
    try {
      return await this.request(move(query));
    } catch (e) {
      return e;
    }
  };
  viewFileApi = async (query: any) => {
    try {
      return await this.request(viewFile(query));
    } catch (e) {
      return e;
    }
  };

  startFileUpload = async (query: any) => {
    try {
      return await this.request(startFileUpload(query));
    } catch (e) {
      return e;
    }
  };

  completeFileUpload = async (query: any) => {
    try {
      return await this.request(completeFileUpload(query));
    } catch (e) {
      return e;
    }
  };

  requestAccess = async (query: any) => {
    try {
      return await this.request(requestAccess(query));
    } catch (e) {
      return e;
    }
  };
  getAllFolders = async () => {
    try {
      const result = await this.request(
        queryLibraryFolderItems({}),
      );
      return result;
    } catch (e) {
      const error: any = e;
      return error;
    }
  };
}
